import { useState, useCallback, useEffect } from 'react'
import type { AuthState, User, AuthFormErrors } from '../types/auth'
import { validateEmail, validatePassword, validateConfirmPassword } from '../utils/validation'
import { authService } from '../services/auth.service'
import { isSupabaseAuthEnabled } from '../lib/supabase'
import { amplitudeService } from '../services/amplitude.service'

const useSupabaseAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true, // Start with loading to check for existing session
    error: null,
    isAuthenticated: false,
  })

  // Check for existing session on mount
  useEffect(() => {
    if (!isSupabaseAuthEnabled) {
      console.log('🔧 Auth Debug: Supabase auth is disabled')
      setState((prev) => ({ ...prev, isLoading: false }))
      return
    }

    const checkSession = async () => {
      try {
        console.log('🔍 Auth Debug: Checking for existing session...')
        const session = await authService.getSession()

        if (session) {
          console.log('✅ Auth Debug: Existing session found:', {
            userId: session.user.id,
            email: session.user.email,
            provider: session.session?.app_metadata?.provider,
            expiresAt: session.session?.expires_at,
          })
          setState({
            user: session.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } else {
          console.log('❌ Auth Debug: No existing session found')
          setState((prev) => ({ ...prev, isLoading: false }))
        }
      } catch (error) {
        console.error('❌ Auth Debug: Session check error:', error)
        setState((prev) => ({ ...prev, isLoading: false }))
      }
    }

    checkSession()

    // Subscribe to auth state changes
    const subscription = authService.onAuthStateChange((user) => {
      setState((prev) => ({
        ...prev,
        user,
        isAuthenticated: !!user,
        isLoading: false,
      }))
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
  }, [])

  const setLoading = useCallback((isLoading: boolean) => {
    setState((prev) => ({ ...prev, isLoading }))
  }, [])

  const setError = useCallback((error: string) => {
    setState((prev) => ({ ...prev, error, isLoading: false }))
  }, [])

  const setUser = useCallback((user: User | null) => {
    setState((prev) => ({
      ...prev,
      user,
      isAuthenticated: !!user,
      isLoading: false,
      error: null,
    }))

    // Identify user in Amplitude when they log in
    if (user) {
      amplitudeService.identify(user.id, {
        email: user.email,
        name: user.name,
        createdAt: user.createdAt.toISOString(),
      })
    }
  }, [])

  // Login function
  const login = useCallback(
    async (email: string, password: string) => {
      if (!isSupabaseAuthEnabled) {
        console.warn('Supabase auth is disabled. Using mock auth.')
        return
      }

      setLoading(true)
      clearError()

      try {
        const { user } = await authService.signIn(email, password)
        setUser(user)
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Invalid email or password. Please try again.'
        setError(errorMessage)
        throw error
      }
    },
    [setLoading, clearError, setUser, setError]
  )

  // Signup function
  const signup = useCallback(
    async (email: string, password: string) => {
      if (!isSupabaseAuthEnabled) {
        console.warn('Supabase auth is disabled. Using mock auth.')
        return
      }

      setLoading(true)
      clearError()

      try {
        const { user } = await authService.signUp(email, password)
        setUser(user)
        // Note: Supabase may require email confirmation
        // You might want to show a message about checking email
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to create account. Please try again.'
        setError(errorMessage)
        throw error
      }
    },
    [setLoading, clearError, setUser, setError]
  )

  // Google OAuth function
  const loginWithGoogle = useCallback(async () => {
    if (!isSupabaseAuthEnabled) {
      console.warn('🔧 Auth Debug: Supabase auth is disabled. Using mock auth.')
      return
    }

    console.log('🔍 Auth Debug: Starting Google OAuth flow...')
    setLoading(true)
    clearError()

    try {
      console.log('🔍 Auth Debug: Calling authService.signInWithGoogle()')
      const result = await authService.signInWithGoogle()
      console.log('🔍 Auth Debug: Google OAuth response:', result)
      // Note: OAuth will redirect, so loading state might persist
      // The auth state change listener will handle the user update
    } catch (error: unknown) {
      console.error('❌ Auth Debug: Google OAuth error:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Google authentication failed. Please try again.'
      setError(errorMessage)
      setLoading(false)
      throw error
    }
  }, [setLoading, clearError, setError])

  // Logout function
  const logout = useCallback(async () => {
    if (!isSupabaseAuthEnabled) {
      console.warn('🔧 Auth Debug: Supabase auth is disabled. Using mock auth.')
      setUser(null)
      amplitudeService.reset() // Reset Amplitude user
      return
    }

    console.log('🔍 Auth Debug: Starting logout process...')
    setLoading(true)

    try {
      await authService.signOut()

      // Manually clear localStorage to ensure session is completely removed
      console.log('🔍 Auth Debug: Clearing localStorage...')
      localStorage.removeItem('askanything-auth')
      localStorage.removeItem('sb-nzjwhkwttovwscwkojbp-auth-token')

      // Clear any other Supabase related storage
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('supabase') || key.includes('auth')) {
          console.log('🔍 Auth Debug: Removing localStorage key:', key)
          localStorage.removeItem(key)
        }
      })

      setUser(null)
      amplitudeService.reset() // Reset Amplitude user
      console.log('✅ Auth Debug: Logout completed successfully')
    } catch (error: unknown) {
      console.error('❌ Auth Debug: Logout error:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to logout. Please try again.'
      setError(errorMessage)
      throw error
    }
  }, [setLoading, setUser, setError])

  // Form validation helper
  const validateForm = useCallback(
    (
      email: string,
      password: string,
      confirmPassword?: string,
      mode: 'login' | 'signup' = 'login'
    ): AuthFormErrors => {
      const errors: AuthFormErrors = {}

      const emailError = validateEmail(email)
      if (emailError) errors.email = emailError

      const passwordError = validatePassword(password)
      if (passwordError) errors.password = passwordError

      if (mode === 'signup' && confirmPassword !== undefined) {
        const confirmError = validateConfirmPassword(password, confirmPassword)
        if (confirmError) errors.confirmPassword = confirmError
      }

      return errors
    },
    []
  )

  return {
    ...state,
    login,
    signup,
    loginWithGoogle,
    logout,
    clearError,
    validateForm,
  }
}

export default useSupabaseAuth
